// GAVION training (« Formation », /formation). Rules quoted here are taught from the dialer code: the numbers come in
// as %{arguments} computed from the dialer constants (training/content/*.ts), never typed by hand.
import { productMissionsFr, trainingProductFr } from "./trainingProduct.fr";
export const trainingFr = {
  title: "Formation",
  subtitle:
    "Deviens Pro du composeur : des mini-jeux sur les règles du composeur, et des missions qui se valident toutes seules quand tu appelles.",
  load_error: "Impossible de charger ta progression.",
  retry: "Réessayer",
  // GAVION gavion-context: /formation/gavion « Tout sur Gavion » (every signed-in user, read only)
  gavion_page: {
    link: "Tout sur Gavion",
    title: "Tout sur Gavion",
    subtitle:
      "Gavion, Gala, les forfaits, les conditions et les réponses aux objections : ce que l'IA sait aussi avant d'écrire.",
    back: "Retour à la formation",
    load_error: "Impossible de charger le contexte.",
  },
  tabs: {
    label: "Vue de la formation",
    me: "Ma progression",
    team: "Équipe",
  },
  header: {
    level: "Niveau %{level}",
    xp_total: "%{xp} XP",
    xp_bar: "Progression vers le niveau %{level}",
    xp_next: "%{remaining} XP avant le niveau %{level}",
    missions_done: "%{done}/%{total} missions",
    badges: "Badges",
    level_short: "Niveau",
    badge_earned: "obtenu",
    badge_locked: "à obtenir",
  },
  ranks: {
    recruit: "Recrue",
    prospector: "Prospecteur",
    closer: "Closer",
    pro: "Pro du composeur",
  },
  badges: {
    first_step: {
      name: "Premier pas",
      desc: "Une première mission réussie.",
    },
    flawless: { name: "Sans faute", desc: "Un quiz réussi à 100 %." },
    queue_master: {
      name: "Maître de la file",
      desc: "« Lis la file » sans une erreur.",
    },
    fire_fingers: {
      name: "Doigts de feu",
      desc: "90 points ou plus aux raccourcis.",
    },
    cool_head: {
      name: "Sang-froid",
      desc: "« Situations de crise » sans une erreur.",
    },
    field_ready: {
      name: "Sur le terrain",
      desc: "Toutes les missions terrain réussies.",
    },
    graduate: {
      name: "Diplômé",
      desc: "Toute la formation « Vente au téléphone » terminée.",
    },
    gavion_seller: {
      name: "Vendeur Gavion",
      desc: "Tout « Vendre Gavion » réussi.",
    },
    gavion_expert: {
      name: "Expert Gavion",
      desc: "Tout « Gavion à fond » réussi.",
    },
  },
  states: {
    locked: "Verrouillée",
    available: "À faire",
    in_progress: "En cours",
    done: "Réussie",
  },
  map: {
    title: "Carte des missions",
    kind_game: "Mini-jeu",
    kind_field: "Sur le terrain",
    play: "Jouer",
    replay: "Rejouer",
    locked_hint: "Réussis la mission précédente pour la débloquer.",
    auto_hint: "Se valide toute seule quand tes appels le montrent.",
    best: "Meilleur score : %{score} %",
    attempts: "%{smart_count} essai |||| %{smart_count} essais",
    reward: "+%{xp} XP",
    bonus: "+%{xp} XP si parfait",
    progress: "%{count} / %{target}",
  },
  missions: {
    ready: {
      title: "Prêt à composer",
      desc: "Vérifie ton softphone dans Paramètres → Téléphone (bouton de test, puis « J'ai entendu l'appel »).",
      cta: "Configurer le téléphone",
    },
    golden_hours: {
      title: "Les heures d'or",
      desc: "Quand as-tu le droit d'appeler ? %{count} questions sur les heures permises (CRTC).",
    },
    read_queue: {
      title: "Lis la file",
      desc: "Remets 4 compagnies dans l'ordre où la file te les sert. %{count} manches.",
    },
    right_outcome: {
      title: "Le bon résultat",
      desc: "Une situation d'appel, 8 résultats possibles : choisis le bon. %{count} manches.",
    },
    hotkeys: {
      title: "Doigts de feu",
      desc: "%{count} situations, une touche chacune. Vite et juste !",
    },
    crisis: {
      title: "Situations de crise",
      desc: "Pause, hors ligne, compagnie libérée… Que fait le composeur ? %{count} questions.",
    },
    first_session: {
      title: "Première session",
      desc: "Lance une session d'appels.",
      cta: "Appeler",
    },
    ten_calls: {
      title: "10 appels",
      desc: "Enregistre 10 appels avec un résultat.",
      cta: "Appeler",
    },
    first_contact: {
      title: "Premier contact",
      desc: "Parle à une vraie personne : Parlé – rappeler, Démo réservée, Brochure envoyée ou Pas intéressé.",
      cta: "Appeler",
    },
    first_callback: {
      title: "Premier rappel planifié",
      desc: "Enregistre un « Parlé – rappeler » avec une date de rappel.",
      cta: "Appeler",
    },
    first_demo: {
      title: "Première démo",
      desc: "Réserve une démo (résultat Démo réservée ou deal avec une date de démo).",
      cta: "Appeler",
    },
    contact_call: {
      title: "Appel à un contact",
      desc: "Appelle un contact depuis sa fiche (bouton Appeler).",
      cta: "Voir les contacts",
    },
    zero_unlogged: {
      title: "Zéro oubli",
      desc: "Aucun appel en attente dans « À compléter » (après au moins un appel enregistré).",
      cta: "Ouvrir « À compléter »",
    },
    ...productMissionsFr,
  },
  game: {
    question: "Question %{n} sur %{total}",
    round: "Manche %{n} sur %{total}",
    correct: "Bonne réponse !",
    wrong: "Pas tout à fait.",
    answer_was: "La bonne réponse : %{answer}",
    right_option: "bonne réponse",
    your_option: "ta réponse",
    next: "Suivant",
    finish: "Voir mon score",
    score_title: "Ton score",
    score: "%{correct} / %{total} — %{score} %",
    passed: "Mission réussie !",
    failed: "Il faut %{pass} % pour réussir. Réessaie !",
    perfect: "Parfait ! Bonus de %{xp} XP.",
    xp_gain: "+%{xp} XP",
    retry: "Rejouer",
    back: "Retour à la carte",
    quit: "Quitter le jeu",
    saving: "Enregistrement…",
    save_failed:
      "Ton score n'a pas pu être enregistré. Vérifie ta connexion et réessaie.",
    celebration: "Mission réussie : %{mission}",
  },
  order: {
    instructions:
      "Mets ces compagnies dans l'ordre où la file te les sert : la première servie en haut. Utilise Monter et Descendre.",
    position: "Position %{n}",
    move_up: "Monter %{name}",
    move_down: "Descendre %{name}",
    check: "Valider l'ordre",
    right: "Ordre parfait !",
    wrong: "Pas tout à fait. Voici l'ordre de la file :",
    rule_title: "La règle de la file",
    rule: {
      1: "Mes rappels dus (les plus anciens d'abord)",
      2: "Les rappels dus non assignés",
      3: "Jamais appelées : les miennes, puis les non assignées ; haute qualité, puis meilleur score",
      4: "Relances : dernier appel il y a plus de %{hours} h (le plus ancien d'abord)",
    },
  },
  hotkeys: {
    intro:
      "Une situation s'affiche : appuie sur le raccourci du composeur. Tu as %{seconds} secondes par situation. Le clavier n'est capté que pendant la partie ; Échap pour abandonner.",
    start: "Commencer la partie",
    area: "Zone de jeu des raccourcis",
    press: "Appuie sur la bonne touche",
    progress: "Situation %{n} sur %{total}",
    hit: "Bien joué !",
    miss: "Raté : c'était %{key}.",
    timeout: "Trop tard : c'était %{key}.",
    accuracy: "Précision : %{percent} %",
    speed: "Temps moyen : %{seconds} s",
    score: "%{score} points",
    record: "Record : %{score} points",
    new_record: "Nouveau record !",
    quit: "Abandonner (Échap)",
    keys_title: "Les raccourcis du composeur",
  },
  content: {
    golden_hours: {
      yes: "Oui, j'appelle",
      no: "Non, j'attends",
      tue_late: {
        prompt:
          "Il est %{time} à Montréal, un %{day}. Ta compagnie est à Montréal. Tu peux l'appeler ?",
        explanation:
          "Non. En semaine, la plage est de %{weekday_start} à %{weekday_end}, à l'heure locale de la compagnie. À %{lead_time}, c'est trop tard : le composeur ne te la servira pas.",
      },
      sat_morning: {
        prompt:
          "Il est %{time} à Québec, un %{day}. Ta compagnie est à Québec. Tu peux l'appeler ?",
        explanation:
          "Oui. La fin de semaine, on appelle de %{weekend_start} à %{weekend_end}. %{lead_time}, c'est dans la plage.",
      },
      vancouver: {
        prompt:
          "Il est %{time} à Montréal, un %{day}. Ta compagnie est à Vancouver. Tu peux l'appeler ?",
        explanation:
          "Non. Ce qui compte, c'est l'heure de la compagnie : il est %{lead_time} à Vancouver, avant %{weekday_start}. Le composeur vérifie l'heure locale de chaque compagnie.",
      },
      sun_close: {
        prompt:
          "Il est %{time} pile à Toronto, un %{day}. Ta compagnie est à Toronto. Tu peux l'appeler ?",
        explanation:
          "Non. La plage de fin de semaine finit à %{weekend_end}, et l'heure de fin est exclue : à %{lead_time} pile, c'est déjà fermé. Ouverture à %{weekend_start}.",
      },
      holidays: {
        prompt:
          "C'est le 25 décembre (ou le 1er janvier), 10 h 30. Que fait le composeur ?",
        blocked:
          "Il n'appelle personne de la journée, selon la date locale de la compagnie : la prochaine plage est le lendemain à l'ouverture.",
        weekend_hours: "Il appelle avec les heures de fin de semaine.",
        config_list:
          "Ça dépend de la liste des jours fériés dans les paramètres d'appel.",
        explanation:
          "Le 25 décembre et le 1er janvier, aucun appel, chaque année, selon la date locale de la compagnie : le 24 au soir, une compagnie de Vancouver peut encore être appelée alors qu'il est déjà Noël à Halifax. Le composeur affiche « jour férié » et la prochaine plage (le lendemain à l'ouverture). La liste des jours fériés des paramètres ne débloque jamais ces deux jours : elle sert à compter les jours ouvrables des automatisations.",
      },
    },
    read_queue: {
      cards: {
        callback_mine: "Rappel dû il y a %{hours} h, assigné à toi",
        callback_unassigned: "Rappel dû il y a %{hours} h, non assigné",
        callback_unassigned_min: "Rappel dû il y a %{minutes} min, non assigné",
        never_hq: "Jamais appelé, haute qualité",
        never_hq_score: "Jamais appelé, haute qualité, score %{score}",
        never_hq_unassigned: "Jamais appelé, haute qualité, non assigné",
        never_score: "Jamais appelé, score %{score}",
        never_mine_score: "Jamais appelé, score %{score}, assigné à toi",
        retry: "Relance : dernier appel il y a %{hours} h",
      },
      r1: {
        explanation:
          "Tes rappels dus passent avant tout, puis les rappels dus non assignés, puis les compagnies jamais appelées, et enfin les relances.",
      },
      r2: {
        explanation:
          "Un rappel dû passe en premier. Parmi les compagnies jamais appelées, la haute qualité passe avant le score : une compagnie haute qualité à 20 passe avant une compagnie ordinaire à 40.",
      },
      r3: {
        explanation:
          "Parmi les jamais appelées, les compagnies assignées à toi passent avant les non assignées, même haute qualité. Les relances viennent en dernier, la plus ancienne d'abord (au moins %{hours} h après le dernier appel).",
      },
    },
    right_outcome: {
      rings_out: {
        prompt:
          "Ça sonne… sonne… sonne. Personne ne répond, pas de boîte vocale.",
        explanation:
          "Pas de réponse (%{key}) : personne au bout du fil et aucun message laissé. La compagnie reste « en cours » et reviendra dans la file.",
      },
      voicemail: {
        prompt:
          "« Vous êtes bien chez Plomberie Gagnon, laissez un message. » Tu laisses ton message après le bip.",
        explanation:
          "Message laissé (%{key}) : tu as laissé un message sur la boîte vocale. La compagnie reste « en cours ».",
      },
      callback_thursday: {
        prompt: "« Je suis sur un chantier. Rappelez-moi jeudi vers 14 h. »",
        explanation:
          "Parlé – rappeler (%{key}), avec la date et l'heure du rappel : obligatoire, dans le futur et dans les heures permises de la compagnie. La file te la resservira à ce moment-là.",
      },
      demo_tuesday: {
        prompt: "« OK, montrez-moi ça. Mardi 10 h, ça vous va ? »",
        explanation:
          "Démo réservée (%{key}) : indique la date et l'heure, et une durée de %{durations} minutes. La compagnie sort de la file d'appels.",
      },
      send_brochure: {
        prompt:
          "« Envoyez-moi votre brochure par courriel, je vais regarder ça. »",
        explanation:
          "Brochure envoyée (%{key}) : un courriel valide est obligatoire si la compagnie n'en a pas déjà un.",
      },
      simple_no: {
        prompt:
          "« Non merci, ça ne m'intéresse pas. » Et il raccroche poliment.",
        explanation:
          "Pas intéressé (%{key}), avec une raison : la compagnie est mise en pause %{days} jours. Ne pas appeler, c'est seulement quand le prospect demande explicitement de ne plus être appelé.",
      },
      wrong_company: {
        prompt:
          "« Ici le Garage Tremblay… Non, pas de plomberie ici, vous vous êtes trompé. »",
        explanation:
          "Mauvais numéro (%{key}) : le numéro est marqué invalide et la compagnie sort de la file d'appels.",
      },
      never_again: {
        prompt:
          "« Retirez mon numéro de votre liste. Ne m'appelez plus jamais. »",
        explanation:
          "Ne pas appeler (%{key}) : c'est permanent. Tu confirmes la demande du prospect, et son numéro (et son courriel, s'il en a un) va sur la liste Ne pas appeler. À ne pas confondre avec Pas intéressé (pause de %{days} jours).",
      },
      consent: {
        prompt:
          "Le prospect dit : « Envoyez-moi la brochure. » Rien d'autre sur vos courriels. Tu choisis Brochure envoyée. La case « %{box} » ?",
        tick: "Je la coche : il veut la brochure",
        leave: "Je la laisse vide : il n'a pas dit oui à nos courriels",
        explanation:
          "La case « %{box} » enregistre un consentement exprès (LCAP). Coche-la seulement quand le prospect accepte clairement de recevoir vos courriels.",
      },
    },
    crisis: {
      long_pause: {
        prompt:
          "Tu mets la session en pause (Espace) pour un café… et tu reviens %{minutes} minutes plus tard. La compagnie à l'écran ?",
        released: "Elle a été libérée : elle retourne dans la file",
        kept: "Elle reste à toi tant que la session est ouverte",
        session_ends: "La session se termine toute seule",
        explanation:
          "Après %{minutes} minutes de pause sur une même fiche, la compagnie est libérée pour les autres. Le composeur te prévient.",
      },
      offline: {
        prompt:
          "Ton Wi-Fi tombe juste au moment où tu enregistres le résultat d'un appel.",
        queued: "Le résultat est gardé et envoyé à la reconnexion",
        lost: "Le résultat est perdu, il faut tout refaire",
        queue_moves: "La file continue d'avancer comme si de rien n'était",
        explanation:
          "Hors ligne, les résultats sont gardés sur ton poste (jusqu'à %{max}) et envoyés dans l'ordre à la reconnexion. La file n'avance pas tant que tu es hors ligne. Seul le consentement courriel n'est pas enregistré hors ligne : refais-le.",
      },
      lease_lost: {
        prompt:
          "Tu viens de parler au prospect. En enregistrant, une fenêtre « %{title} » s'ouvre.",
        save_anyway: "Enregistrer quand même",
        skip: "Passer",
        close_tab: "Fermer l'onglet",
        explanation:
          "Le verrou a expiré, mais tu as vraiment appelé : choisis « %{save} » pour garder le résultat de ton appel. « Passer », c'est seulement si tu n'as pas appelé.",
      },
      crash: {
        prompt:
          "Ton navigateur plante en plein appel, avant que tu choisisses un résultat.",
        to_complete:
          "L'appel devient « Non consigné » et m'attend dans « À compléter »",
        lost: "L'appel disparaît",
        dnc: "La compagnie passe en Ne pas appeler",
        explanation:
          "Un appel sans résultat devient « Non consigné » après %{minutes} minutes et apparaît dans « %{list} ». Ouvre la liste et complète le résultat.",
      },
      wrong_outcome: {
        prompt:
          "Il y a 2 heures, tu as choisi Pas de réponse par erreur : en fait, tu avais laissé un message.",
        edit: "Je corrige le résultat depuis l'historique de la compagnie",
        call_again: "Je rappelle la compagnie pour corriger",
        impossible: "On ne peut plus rien y faire",
        explanation:
          "Tu peux modifier le résultat de ton propre appel pendant %{hours} h : bouton « %{action} » sur l'appel dans l'historique.",
      },
      locked: {
        prompt: "Sur la fiche d'une compagnie, tu vois « En cours — Martin ».",
        dont_call: "Je n'appelle pas : Martin est en train de l'appeler",
        call_anyway: "J'appelle quand même, premier arrivé, premier servi",
        dnc_list: "La compagnie est sur la liste Ne pas appeler",
        explanation:
          "« En cours » veut dire qu'un autre représentant tient cette compagnie en ce moment. Le bouton Appeler est désactivé : on ne l'appelle pas en même temps.",
      },
      recording: {
        prompt:
          "Un prospect te demande : « Est-ce que vous enregistrez l'appel ? »",
        no_recording:
          "Non : le CRM n'enregistre aucun appel, mes notes sont prises dans Granola",
        read_notice: "Je lis d'abord l'avis d'enregistrement du script",
        yes_quality: "Oui, pour la qualité, mais il peut refuser",
        explanation:
          "Le CRM n'enregistre aucun appel : la fonction a été retirée. Il n'y a ni avis d'enregistrement à lire ni case « refuse l'enregistrement ». Tes notes d'appel sont prises dans Granola. %{toggle}",
      },
    },
    hotkeys: {
      prompts: {
        rings_out: "Ça sonne dans le vide, pas de boîte vocale",
        left_message: "Tu as laissé un message sur la boîte vocale",
        call_me_thursday: "« Rappelez-moi jeudi »",
        demo_yes: "« OK pour une démo mardi ! »",
        send_info: "« Envoyez-moi la brochure »",
        not_interested: "« Non merci, pas intéressé »",
        wrong_company: "Mauvaise entreprise au bout du fil",
        never_again: "« Ne m'appelez plus jamais »",
        skip: "Passer cette compagnie sans l'appeler",
        note: "Écrire une note",
        objections: "Chercher une réponse à une objection",
        space: "Pause café (ou annuler le compte à rebours)",
        save: "Enregistrer le résultat",
        help: "Afficher l'aide des raccourcis",
      },
      keys: {
        space: "Espace",
        ctrl_enter: "Ctrl+Entrée",
      },
    },
  },
  team: {
    title: "Progression de l'équipe",
    name: "Représentant",
    level: "Niveau",
    missions: "Missions",
    xp: "XP",
    last_activity: "Dernière activité",
    never: "Jamais",
    empty: "Aucun utilisateur actif.",
    product: "Produit",
  },
  nudge: {
    title: "Formation : %{done}/%{total} missions",
    body: "Termine les missions pour devenir Pro du composeur.",
    cta: "Continuer la formation",
    dismiss: "Masquer la formation du tableau de bord",
  },
  dialer: {
    link: "Formation",
    hint: "Formation du composeur : %{done}/%{total} missions.",
    hint_cta: "Continuer",
  },
  // GAVION product-training: Formation → « Produit » (spec 2026-11-02-product-training-design.md)
  product: {
    ...trainingProductFr,
    catalog: {
      option: "%{value}",
      price_prompt: "Combien coûte « %{name} » par mois?",
      package_prompt: "Quel forfait coûte %{price} par mois?",
      addon_prompt: "Combien coûte l'option « %{name} »?",
      explanation: "Prix en dollars canadiens, par mois, avant TPS et TVQ.",
      addon_unit_explanation:
        "Facturé à l'unité utilisée dans le mois, avant TPS et TVQ.",
    },
  },
};
